import { X } from 'lucide-react';

import type { NoteView } from '@/features/Visualizer/types';

interface Props {
  value: NoteView;
  onChange: (next: NoteView) => void;
}

/**
 * Editor for the generic `note` view — a labeled box with freeform
 * key/value attributes, for diagrams that aren't array/tree/graph-shaped
 * (e.g. "Load balancer", "Cache"). Kept ordered via the same
 * rebuild-from-entries pattern as `StateKeyValueEditor`, for the same
 * reason: an object literal has no stable key order to rely on.
 */
export function VisualizationNoteCanvas({ value, onChange }: Props) {
  const entries: Array<[string, string]> = Object.entries(value.attributes ?? {});

  const setLabel = (label: string) => onChange({ ...value, label });

  const emitEntries = (nextEntries: Array<[string, string]>) => {
    onChange({ ...value, attributes: Object.fromEntries(nextEntries) });
  };

  const setEntryKey = (index: number, key: string) => {
    const next = [...entries];
    next[index] = [key, next[index][1]];
    emitEntries(next);
  };

  const setEntryValue = (index: number, val: string) => {
    const next = [...entries];
    next[index] = [next[index][0], val];
    emitEntries(next);
  };

  const removeEntry = (index: number) => {
    emitEntries(entries.filter((_, i) => i !== index));
  };

  return (
    <div className="rounded-md border border-[#ececec] p-3">
      <label className="mb-1 block text-xs font-medium text-[#374151]" htmlFor="note-label">
        Label
      </label>
      <input
        id="note-label"
        value={value.label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="e.g. Cache, Load balancer, Queue worker"
        className="mb-3 w-full rounded-md border border-[#d1d5db] px-2 py-1 text-sm"
      />

      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-[#374151]">Attributes</span>
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={() => emitEntries([...entries, ['', '']])}
        >
          + Add attribute
        </button>
      </div>
      {entries.length === 0 && (
        <p className="text-[11px] text-[#9ca3af]">
          No attributes yet — add one to show it under the label (e.g. "capacity: 3 nodes").
        </p>
      )}
      {entries.map(([key, val], index) => (
        <div key={index} className="mb-1 flex items-center gap-1">
          <input
            value={key}
            onChange={(e) => setEntryKey(index, e.target.value)}
            placeholder="name"
            className="w-28 rounded-md border border-[#d1d5db] px-2 py-1 text-sm"
          />
          <input
            value={val}
            onChange={(e) => setEntryValue(index, e.target.value)}
            placeholder="value"
            className="w-full rounded-md border border-[#d1d5db] px-2 py-1 text-sm"
          />
          <button
            type="button"
            aria-label={`Remove attribute ${key || `#${index + 1}`}`}
            onClick={() => removeEntry(index)}
            className="shrink-0 text-destructive"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  );
}
